import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Paper,
  Select,
  MenuItem,
  Alert
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useState } from "react";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { useAuth } from "../../../context/AuthContext";
import api from "../../../services/api";

interface LineItem {
  account: string;
  debit: number;
  credit: number;
  description?: string;
}

const MODULES = [
  "Manual",
  "POS",
  "Invoice",
  "Purchases",
  "Payroll",
  "Tax",
  "Expense",
  "Finance",
];

const ACCOUNTS = [
  // Assets
  "Cash",
  "Bank",
  "Mobile Money",
  "Accounts Receivable",
  "Inventory",
  "Equipment",
  // Liabilities
  "Accounts Payable",
  "Tax Payable",
  "Loan Payable (Short-Term)",
  "Loan Payable (Long-Term)",
  // Income
  "Sales Revenue",
  // Expenses
  "Cost of Goods Sold",
  "Interest Expense",
  "Owner's Equity",
  "Owner's Drawings",
  "Salaries Expense",
  "Rent Expense",
  "Utilities Expense",
  "Other Expense",
] as const;

export default function CreateJournalPage() {
  useProtectedRoute();
  const { token } = useAuth();
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [moduleName, setModuleName] = useState<string>("Manual");
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState<LineItem[]>([
    { account: "", debit: 0, credit: 0, description: "" },
  ]);

  const addLine = () => setLines([...lines, { account: "", debit: 0, credit: 0, description: "" }]);

  const updateLine = (index: number, field: keyof LineItem, value: string | number) => {
    const updated = [...lines];
    if (field === "debit" || field === "credit") updated[index][field] = parseFloat(value as string) || 0;
    else updated[index][field] = value as string;
    setLines(updated);
  };

  const removeLine = (index: number) => setLines(lines.filter((_, i) => i !== index));

  const getTotal = (type: "debit" | "credit") => lines.reduce((acc, l) => acc + (type === "debit" ? l.debit : l.credit), 0);
  const isBalanced = Math.abs(getTotal("debit") - getTotal("credit")) < 0.0001;

  const normalizeLines = () => lines.map((l) => ({ ...l, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 }));

  const handleSubmit = async () => {
    if (!date || !description || lines.length === 0) return alert("All fields are required.");
    if (!isBalanced) return alert("Journal entry is not balanced. Debits must equal credits.");
    try {
      await api.post(
        "/journals",
        { date, description, module: moduleName, lines: normalizeLines() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Journal entry created.");
      window.location.href = "/accounts/journals";
    } catch (err: any) {
      alert("Error saving journal: " + (err?.response?.data?.message || "Unknown error"));
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h5" mb={2} fontWeight="bold">Add Journal Entry</Typography>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <TextField type="date" label="Date" InputLabelProps={{ shrink: true }} fullWidth value={date} onChange={(e) => setDate(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Select fullWidth value={moduleName} onChange={(e) => setModuleName(e.target.value as string)}>
            {MODULES.map((m) => (<MenuItem key={m} value={m}>{m}</MenuItem>))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField label="Entry Description" fullWidth value={description} onChange={(e) => setDescription(e.target.value)} />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" mb={1}>Line Items</Typography>

      {lines.map((line, idx) => (
        <Paper key={idx} className="p-4 mb-3">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Autocomplete
                options={Array.from(ACCOUNTS)}
                freeSolo
                value={line.account}
                onChange={(_, v) => updateLine(idx, "account", v || "")}
                onInputChange={(_, v) => updateLine(idx, "account", v || "")}
                renderInput={(params) => (<TextField {...params} label="Account" fullWidth />)}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Debit"
                type="number"
                fullWidth
                value={line.debit}
                onChange={(e) => { const v = e.target.value; updateLine(idx, "debit", v); if ((parseFloat(v) || 0) > 0) updateLine(idx, "credit", 0); }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Credit"
                type="number"
                fullWidth
                value={line.credit}
                onChange={(e) => { const v = e.target.value; updateLine(idx, "credit", v); if ((parseFloat(v) || 0) > 0) updateLine(idx, "debit", 0); }}
              />
            </Grid>
            <Grid item xs={10} sm={4}>
              <TextField label="Description" fullWidth value={line.description} onChange={(e) => updateLine(idx, "description", e.target.value)} />
            </Grid>
            <Grid item xs={2} sm={1} display="flex" alignItems="center">
              <IconButton onClick={() => removeLine(idx)} color="error"><DeleteIcon /></IconButton>
            </Grid>
          </Grid>
        </Paper>
      ))}

      <Button variant="outlined" onClick={addLine} startIcon={<AddIcon />} className="mb-6">Add Line</Button>

      <Box mb={2}>
        <Typography fontWeight="bold">Total Debit: ${getTotal("debit").toFixed(2)} | Total Credit: ${getTotal("credit").toFixed(2)}</Typography>
        {isBalanced ? (
          <Alert severity="success" sx={{ mt: 1 }}>Entry is balanced</Alert>
        ) : (
          <Alert severity="warning" sx={{ mt: 1 }}>Debits and Credits must match</Alert>
        )}
      </Box>

      <Box mt={2}>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!isBalanced}>Save Journal Entry</Button>
      </Box>
    </Box>
  );
}
